/**
 * World Garden's progression content: metrics, XP, ranks, rewards, challenges.
 *
 * ## This is the file a new project rewrites
 *
 * The engine in `src/progression/` and the journal in `src/ui/journal.ts`
 * know nothing about gardens. Everything that does is here, as data. To
 * rebrand for another game:
 *
 *   1. Replace `Metric` with the things that project's players do.
 *   2. Bump those counters at its events (in this game, `ProgressionAdapter`
 *      in `src/app/progression.ts`, called from `main.ts`).
 *   3. Re-weight `XP_WEIGHTS`, retitle `RANKS`, and rewrite `CHALLENGES`.
 *   4. Rewrite `JOURNAL_COPY`.
 *
 * Arabic strings here are working translations and **need native review
 * before launch**, exactly as the garden names in `src/world/record.ts` do.
 *
 * ## One rule that is not optional
 *
 * **Publishing to the world map must never be a metric.** No XP, no
 * challenge, no rank for putting a garden on a public map — consent is the
 * lawful basis for publishing, and it stops being freely given the moment
 * refusing costs the player something. `tests/progression.test.ts` fails if a
 * metric that looks like it counts publishing, sharing or signing in appears.
 * The same applies to any future project built on this: never reward a
 * player for giving up data.
 */

import { TOTAL_FACTS } from './facts';
import type { ChallengeDef, LevelCurve, Rank, XpWeights } from '../progression';

/* ------------------------------------------------------------------ *
 * Metrics
 * ------------------------------------------------------------------ */

export type Metric =
  /** Seeds put in the ground, by hand or from a pack. */
  | 'sown'
  /** Taps that tended a growing plant. */
  | 'tended'
  /** Specimens brought to full height. */
  | 'matured'
  /** Specimens lifted for seedlings. */
  | 'harvested'
  /** Seed packs opened. */
  | 'packs'
  /** Specimens that matured carrying a weather mark. */
  | 'marked'
  /** Specimens of rare or legendary rarity that matured. */
  | 'rare'
  /** Fact cards uncovered. */
  | 'facts'
  /** Distinct species ever grown. */
  | 'species'
  /** Distinct weather marks ever grown. */
  | 'mutations'
  /** Badges earned. */
  | 'badges'
  /** Distinct calendar days played. */
  | 'days'
  /** Challenges completed. */
  | 'challenges'
  /** XP banked from completed challenges. */
  | 'challengeXp';

/**
 * Every metric, as a value the tests can walk. Typed as a record so the
 * compiler refuses to let this list and the union above disagree.
 */
export const METRICS: Readonly<Record<Metric, true>> = {
  sown: true,
  tended: true,
  matured: true,
  harvested: true,
  packs: true,
  marked: true,
  rare: true,
  facts: true,
  species: true,
  mutations: true,
  badges: true,
  days: true,
  challenges: true,
  challengeXp: true,
};

/**
 * Metrics the game stores as counters of their own. The rest are read from
 * records the profile already keeps (`treesGrown`, `factsSeen`, `badges`, …)
 * so there is never a second copy of those to disagree with the first.
 */
export const STORED_METRICS = [
  'sown',
  'tended',
  'packs',
  'marked',
  'rare',
  'days',
  'challenges',
  'challengeXp',
] as const satisfies readonly Metric[];

/* ------------------------------------------------------------------ *
 * XP and levels
 * ------------------------------------------------------------------ */

/**
 * What each unit of a metric is worth.
 *
 * `tended` is deliberately absent. It is the one metric a player can drive
 * as fast as they can tap, so it counts toward challenges — which are capped
 * by being claimable once — and never directly toward XP.
 *
 * Tuned so that a first tree lands level 2 (a sow, a mature, the fact card it
 * reveals, a new species, two badges and the day
 * itself ≈ 132 XP), and so that the finite
 * collectables — six species, four marks, fifteen badges, the fact library —
 * are worth about a thousand XP between them: roughly level 5. Everything
 * past that comes from coming back.
 */
export const XP_WEIGHTS: XpWeights<Metric> = {
  sown: 2,
  matured: 10,
  harvested: 5,
  packs: 5,
  marked: 15,
  rare: 20,
  facts: 10,
  species: 25,
  mutations: 40,
  badges: 30,
  days: 25,
  challengeXp: 1,
};

export const LEVEL_CURVE: LevelCurve = { step: 100, maxLevel: 50 };

/**
 * Seedlings paid for each level reached — one seed pack's worth.
 *
 * A level is a moment, and a moment with nothing in it is a number going up.
 * Paying in the currency the player already spends keeps levels meaningful
 * without inventing a second economy to balance.
 */
export const LEVEL_REWARD_SEEDLINGS = 8;

export const RANKS: readonly Rank[] = [
  { from: 1, name: 'New Gardener', nameAr: 'بستاني جديد' },
  { from: 5, name: 'Seed Keeper', nameAr: 'حافظ البذور' },
  { from: 10, name: 'Sapling Tender', nameAr: 'راعي الشتلات' },
  { from: 15, name: 'Palm Grower', nameAr: 'زارع النخيل' },
  { from: 20, name: 'Oasis Keeper', nameAr: 'حارس الواحة' },
  { from: 25, name: 'Wadi Warden', nameAr: 'أمين الوادي' },
  { from: 30, name: 'Grove Master', nameAr: 'سيد البستان' },
  { from: 35, name: 'Green Pioneer', nameAr: 'رائد الخضرة' },
  { from: 40, name: 'Garden Custodian', nameAr: 'أمين الحديقة' },
  { from: 50, name: 'Master Gardener', nameAr: 'كبير البستانيين' },
];

/* ------------------------------------------------------------------ *
 * Challenges
 * ------------------------------------------------------------------ */

export const CHALLENGES_PER_WINDOW = { daily: 3, weekly: 3 } as const;

/** Monday. Set to 0 for a Sunday-start week, 6 for Saturday. */
export const WEEK_STARTS_ON = 1;

/**
 * The pool. Targets are sized against real grow times: an umbrella thorn
 * matures in 45 s and a date palm in 7 min, so "grow three" is one sitting;
 * a sidr takes 45 min and a tamarisk 15, so a rare specimen is a come-back.
 *
 * Ids are stored in saves — never rename one, and never reuse a retired id.
 */
export const CHALLENGES: readonly ChallengeDef<Metric>[] = [
  // Daily ------------------------------------------------------------
  {
    id: 'd-sow-5',
    cadence: 'daily',
    metric: 'sown',
    target: 5,
    xp: 25,
    title: 'Sow 5 seeds',
    titleAr: 'ازرع ٥ بذور',
  },
  {
    id: 'd-grow-3',
    cadence: 'daily',
    metric: 'matured',
    target: 3,
    xp: 40,
    title: 'Grow 3 trees to full height',
    titleAr: 'أنمِ ٣ أشجار حتى تكتمل',
  },
  {
    id: 'd-tend-15',
    cadence: 'daily',
    metric: 'tended',
    target: 15,
    xp: 25,
    title: 'Tend your plants 15 times',
    titleAr: 'اعتنِ بنباتاتك ١٥ مرة',
  },
  {
    id: 'd-fact-1',
    cadence: 'daily',
    metric: 'facts',
    target: 1,
    xp: 30,
    title: 'Uncover a new fact',
    titleAr: 'اكتشف معلومة جديدة عن الحديقة',
    // The library is finite; stop offering this once it is exhausted.
    until: { facts: TOTAL_FACTS },
  },
  {
    id: 'd-harvest-2',
    cadence: 'daily',
    metric: 'harvested',
    target: 2,
    xp: 35,
    title: 'Harvest 2 specimens',
    titleAr: 'احصد نبتتين',
    requires: { matured: 3 },
  },
  {
    id: 'd-pack-1',
    cadence: 'daily',
    metric: 'packs',
    target: 1,
    xp: 30,
    title: 'Open a seed pack',
    titleAr: 'افتح كيس بذور',
    requires: { harvested: 1 },
  },
  {
    id: 'd-rare-1',
    cadence: 'daily',
    metric: 'rare',
    target: 1,
    xp: 50,
    title: 'Grow a rare specimen',
    titleAr: 'أنمِ نبتة نادرة',
    requires: { matured: 5 },
  },
  {
    id: 'd-marked-1',
    cadence: 'daily',
    metric: 'marked',
    target: 1,
    xp: 50,
    title: 'Grow a weather-marked specimen',
    titleAr: 'أنمِ نبتة تحمل أثر الطقس',
    requires: { matured: 5 },
  },

  // Weekly -----------------------------------------------------------
  {
    id: 'w-days-3',
    cadence: 'weekly',
    metric: 'days',
    target: 3,
    xp: 150,
    title: 'Visit your garden on 3 different days',
    titleAr: 'زر حديقتك في ٣ أيام مختلفة',
  },
  {
    id: 'w-sow-30',
    cadence: 'weekly',
    metric: 'sown',
    target: 30,
    xp: 100,
    title: 'Sow 30 seeds',
    titleAr: 'ازرع ٣٠ بذرة',
  },
  {
    id: 'w-grow-20',
    cadence: 'weekly',
    metric: 'matured',
    target: 20,
    xp: 120,
    title: 'Grow 20 trees to full height',
    titleAr: 'أنمِ ٢٠ شجرة حتى تكتمل',
  },
  {
    id: 'w-harvest-10',
    cadence: 'weekly',
    metric: 'harvested',
    target: 10,
    xp: 120,
    title: 'Harvest 10 specimens',
    titleAr: 'احصد ١٠ نباتات',
    requires: { harvested: 1 },
  },
  {
    id: 'w-rare-3',
    cadence: 'weekly',
    metric: 'rare',
    target: 3,
    xp: 150,
    title: 'Grow 3 rare specimens',
    titleAr: 'أنمِ ٣ نباتات نادرة',
    requires: { matured: 10 },
  },
  {
    id: 'w-marked-3',
    cadence: 'weekly',
    metric: 'marked',
    target: 3,
    xp: 150,
    title: 'Grow 3 weather-marked specimens',
    titleAr: 'أنمِ ٣ نباتات تحمل أثر الطقس',
    requires: { matured: 10 },
  },
  {
    id: 'w-packs-3',
    cadence: 'weekly',
    metric: 'packs',
    target: 3,
    xp: 100,
    title: 'Open 3 seed packs',
    titleAr: 'افتح ٣ أكياس بذور',
    requires: { harvested: 5 },
  },
];

/* ------------------------------------------------------------------ *
 * Journal wording
 * ------------------------------------------------------------------ */

export const JOURNAL_COPY = {
  kicker: 'Gardener’s journal',
  tabs: { challenges: 'Challenges', collection: 'Collection', badges: 'Badges' },
  daily: 'Today',
  weekly: 'This week',
  resetsIn: 'resets in',
  noChallenges: 'Nothing to do here yet — sow a seed and check back.',
  xp: 'XP',
  maxLevel: 'Highest level reached',
  levelReward: (seedlings: number) => `Next level pays ${seedlings} seedlings`,
  levelUp: (level: number, rank: string) => `Level ${level} · ${rank}`,
  levelUpBody: (seedlings: number) => `+${seedlings} seedlings banked for your next seed pack.`,
  challengeDone: (cadence: 'daily' | 'weekly') =>
    cadence === 'daily' ? '✓ Daily challenge complete' : '✓ Weekly challenge complete',
  challengeDoneBody: (title: string, xp: number) => `${title} · +${xp} XP`,
  collection: {
    species: 'Species',
    marks: 'Weather marks',
    facts: 'Fact cards',
    lockedFact: 'Not yet uncovered',
  },
  register: 'Garden register',
  close: 'Back to the garden',
} as const;
